import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

export type BaseResponse<T = any> = {
  success: boolean;
  message: string;
  result: T;
  status: number;
};

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<InternalAxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<BaseResponse>>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T>(config: AxiosRequestConfig): Promise<T>;
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const createAxiosInstance = (isPayment = false): CustomInstance => {
  const baseURL = isPayment
    ? process.env.REACT_APP_API_PAYMENT
    : process.env.REACT_APP_API;

  const instance = axios.create({
    baseURL,
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${window.localStorage.getItem("accessToken")}`,
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const accessToken = window.localStorage.getItem("accessToken");
      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      if (response.data.success) {
        return response.data.result;
      } else {
        return Promise.reject(response.data);
      }
    },
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // 🔥 401 → 토큰 재발급 시도
      if (status === 401) {
        const refreshToken = localStorage.getItem("refreshToken");
        const accessToken = localStorage.getItem("accessToken");

        if (refreshToken) {
          try {
            const res = await axios.post(
              `${process.env.REACT_APP_API}/auth/reissue`,
              { refreshToken },
              {
                headers: { Authorization: `Bearer ${accessToken}` },
              }
            );

            const {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            } = res.data.result;

            localStorage.setItem("accessToken", newAccessToken);
            localStorage.setItem("refreshToken", newRefreshToken);

            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            return request(originalRequest);
          } catch (refreshError) {
            // 🔥 refresh 실패 → 강제 로그아웃
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("userInfo");
            localStorage.removeItem("signupStatus");
            window.location.replace("data-room");
            return Promise.reject(refreshError);
          }
        }
      }

      // 🔥 여기 수정 (가장 중요)
      // React Query에서 error.response.status 확인 가능하게 그대로 error 던져야 함
      return Promise.reject(error);
    }
  );

  return instance as CustomInstance;
};

const request = createAxiosInstance();

export default request;
